import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography } from '@mui/material';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Disclaimer from './components/Disclaimer';
import AppointmentModal from './components/AppointmentModal';
import Home from './pages/Home';
import About from './pages/About';
import PracticeAreas from './pages/PracticeAreas';
import Services from './pages/Services';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CaseStudies from './pages/CaseStudies';
import Testimonials from './pages/Testimonials';
import Contact from './pages/Contact';
import Appointment from './pages/Appointment';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

const theme = createTheme({
  palette: {
    primary: {
      // Deep navy
      main: '#102A43',
      light: '#334E68',
      dark: '#0B1F33',
    },
    secondary: {
      // Soft gold accent
      main: '#C7923E',
      light: '#E0B366',
      dark: '#92672A',
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#102A43',
      secondary: '#627D98',
    },
  },
  typography: {
    fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.04em',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: 24,
        },
        containedPrimary: {
          boxShadow: '0 10px 25px rgba(16, 42, 67, 0.25)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
        },
      },
    },
  },
});

function App() {
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const handleDisclaimerAgree = () => {
    setDisclaimerAgreed(true);
    // Always show appointment modal after disclaimer is agreed
    setShowAppointmentModal(true);
  };

  const handleAppointmentClose = () => {
    setShowAppointmentModal(false);
  };

  const handleAppointmentSkip = () => {
    setShowAppointmentModal(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Disclaimer onAgree={handleDisclaimerAgree} />
        <AppointmentModal
          open={showAppointmentModal}
          onClose={handleAppointmentClose}
          onSkip={handleAppointmentSkip}
        />
        {disclaimerAgreed ? (
          <>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/practice-areas" element={<PracticeAreas />} />
              <Route path="/services" element={<Services />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/appointment" element={<Appointment />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Routes>
            <Footer />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Please read and agree to the disclaimer to continue.
            </Typography>
          </Box>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;

